import {
  codeExample,
  IntegrationConfiguration,
  KodemCIPublicImageName,
  type GitHubInstallation,
  type GitHubInstallationDTO,
  type InstallationHealth,
  type Integration,
  type IntegrationDetailsResponse,
  type IntegrationsListResponse,
  type IntegrationStaticInfo,
  type IssueNotificationSettings,
  type JiraMetadata,
  type PreInstallIntegrationResponse
} from "@/api/useIntegrations.types"
import { EXTERNAL_ROUTES, INTEGRATION_TAGS } from "@/const/routes.constants"
import { INTEGRATION_VENDORS } from "@/const/vendors-constants"
import {
  allowsGitHubIssues,
  getWorstHealth,
  toInstallationHealth
} from "@/domain/integration"
import { getConnectedTicketingSystems } from "@/domain/ticketing"
import { isSelfHosted } from "@/helpers/self-hosted.helper"
import { isRecord } from "@/helpers/utils"

const { PUBLIC_DOCS, API_DOCS } = EXTERNAL_ROUTES

const VERSION_PLACEHOLDER = "VERSION_PLACEHOLDER"
export const kodemCIIntegrations = [
  "kodem_ci",
  "jenkins",
  "github_actions",
  "circle_ci"
]
export const kodemCLIIntegrations = ["kodem_cli"]
const ideIntegrations = ["jetbrains", "visualstudio", "vscode"]

export const KODEM_MCP_INTEGRATION_NAME = "kodem_mcp"
export const CLAUDE_CODE_INTEGRATION_NAME = "claude_code"
export const CURSOR_INTEGRATION_NAME = "cursor"
export const GEMINI_CLI_INTEGRATION_NAME = "gemini_cli"
export const GITHUB_COPILOT_INTEGRATION_NAME = "github_copilot"
export const ANTIGRAVITY_INTEGRATION_NAME = "antigravity"
export const CODEX_INTEGRATION_NAME = "codex"
export type CodingAgentName =
  | typeof ANTIGRAVITY_INTEGRATION_NAME
  | typeof CLAUDE_CODE_INTEGRATION_NAME
  | typeof CODEX_INTEGRATION_NAME
  | typeof CURSOR_INTEGRATION_NAME
  | typeof GEMINI_CLI_INTEGRATION_NAME
  | typeof GITHUB_COPILOT_INTEGRATION_NAME
// The Kodem Security plugin's coding-agent integrations share one page layout and a
// per-agent install guide. (The Kodem MCP server is listed with them under AI Agents,
// but is connected, not installed as a plugin.)
export const isCodingAgentIntegration = (integration?: {
  isCodingAgent?: boolean
}) => !!integration?.isCodingAgent

const codingAgentIntegration = ({
  name,
  displayName,
  prerequisites,
  installTitle,
  instructionsLink
}: {
  name: CodingAgentName
  displayName: string
  prerequisites: string[]
  installTitle: string
  instructionsLink?: string
}) => ({
  name,
  isCodingAgent: true,
  displayName,
  description:
    "Scan AI-generated code against your policies, fix your backlog, and get security reports",
  tags: [INTEGRATION_TAGS.aiAgents],
  onlyLogo: true,
  connectPage: {
    sections: [
      {
        title: "About",
        description: `The Kodem Security plugin brings Kodem into ${displayName}: scans AI-generated code with SCA and SAST against your CI and SCM policies before it's committed, fixes your existing backlog ranked by real runtime risk, and reports your posture on demand.`
      },
      {
        title: "Setup",
        prerequisites: [
          ...prerequisites,
          "Network access to GitHub and the Kodem Platform API."
        ],
        additional: [
          {
            noteTitle: "Recommended",
            prerequisites: [
              "CI and SCM policies configured in Kodem (Governance). The Prevention skill enforces the policies you set, without them it has nothing to enforce."
            ]
          }
        ]
      },
      {
        title: installTitle,
        instructionsLink,
        howToUse: { integrationSample: [] }
      }
    ]
  }
})

export const getKodemCICommonExamples = (
  ciImageAndTag: string,
  selfHosted: boolean
): codeExample[] => {
  const selfHostedKodemIntegrationsEndpointCommand = selfHosted
    ? `\n      -e KODEM_INTEGRATIONS_ENDPOINT=$KODEM_INTEGRATIONS_ENDPOINT \\`
    : ""
  const selfHostedDisableResourceUpdatedCommand = selfHosted
    ? `\n      -e KODEM_DISABLE_RESOURCE_UPDATES=true \\`
    : ""
  const kodemServiceKeyLine = `-e KODEM_SERVICE_KEY=$KODEM_SERVICE_KEY \\${selfHostedKodemIntegrationsEndpointCommand}${selfHostedDisableResourceUpdatedCommand}`

  return [
    {
      titleLine1: "Scan container images for open-source vulnerabilities.",
      codeSnippet: `docker run --rm -t \\
      -v /var/run/docker.sock:/var/run/docker.sock \\
      ${kodemServiceKeyLine}
      ${ciImageAndTag} scan image <IMAGE_TO_SCAN>`
    },
    {
      titleLine1:
        "Scan a Container Image and display only High and Critical vulnerabilities while ignoring vulnerabilities with no fixes. <strong>Upload all results to Kodem</strong>.",
      infoLine: "Scan results may take up to 30 minutes to appear in Kodem.",
      codeSnippet: `docker run --rm -t \\
      -v /var/run/docker.sock:/var/run/docker.sock \\
      ${kodemServiceKeyLine}
      ${ciImageAndTag} scan image <IMAGE_TO_SCAN> \\
      --output-severity=critical,high --output-ignore-unfixed --code-repository-name my-repo --upload`
    },
    {
      titleLine1:
        "Scan a Container Image to compare image <p>ubuntu:24.04</p> with an old image  <p>ubuntu:14.04</p>,",
      titleLine2:
        "highlighting new vulnerabilities with High and Critical severities that have a fixed version.",
      codeSnippet: `docker run --rm -t \\
     -v /var/run/docker.sock:/var/run/docker.sock \\
     ${kodemServiceKeyLine}
     ${ciImageAndTag} scan image ubuntu:24.04 \\
     --output-severity=critical,high --output-ignore-unfixed --compare-with ubuntu:14.04`
    },
    {
      titleLine1:
        "Run an open-source scan on a Code Repository and display only High and Critical vulnerabilities while ignoring vulnerabilities with no fixes. <strong>Upload all results to Kodem</strong>.",
      infoLine: "Scan results may take up to 30 minutes to appear in Kodem.",
      codeSnippet: `docker run --rm -t \\
    -v /path/to/<repo>:/tmp/repo \\
    ${kodemServiceKeyLine}
    ${ciImageAndTag} scan code-repository open-source /tmp/repo \\
    --code-repository-name <repo_name> --branch-name <branch_name> --is-default-branch --upload \\
    --output-severity=critical,high --output-ignore-unfixed`
    },
    {
      titleLine1:
        "Run an open-source scan on a Code Repository to compare the current version with an older version of the Code Repository as a baseline, highlighting new vulnerabilities with only High and Critical severities that have a fixed version.",
      codeSnippet: `docker run --rm -t \\
    -v /path/to/<repo>:/tmp/repo \\
    -v /path/to/<baseline_repo>:/tmp/baseline_repo \\
    ${kodemServiceKeyLine}
    ${ciImageAndTag} scan code-repository open-source /tmp/repo \\
    --output-severity=critical,high --output-ignore-unfixed —-compare-with /tmp/baseline_repo`
    },
    {
      titleLine1:
        "Run a code scan on a Code Repository and display only High severity code weaknesses. <strong>Upload all results to Kodem</strong>.",
      infoLine: "Scan results may take up to 30 minutes to appear in Kodem.",
      codeSnippet: `docker run --rm -t \\
    -v /path/to/<repo>:/tmp/repo \\
    ${kodemServiceKeyLine}
    ${ciImageAndTag} scan code-repository code /tmp/repo \\
    --code-repository-name <repo_name> --branch-name <branch_name> --is-default-branch --upload \\
    --output-severity=high`
    },
    {
      titleLine1:
        "Run a code scan on a Code Repository to compare the current version with an older version of the Code Repository as a baseline, highlighting new code weaknesses with High severities.",
      codeSnippet: `docker run --rm -t \\
    -v /path/to/<repo>:/tmp/repo \\
    ${kodemServiceKeyLine}
    ${ciImageAndTag} scan code-repository code /tmp/repo \\
    --output-severity=high --compare-with <baseline-commit-hash>`
    }
  ]
}

export const getKodemCLIExamples = (): codeExample[] => [
  {
    titleLine1: "Scan Code Repository for open-source vulnerabilities.",
    codeSnippet: `kodem-cli scan code-repository open-source /path/to/<repo>`
  },
  {
    titleLine1:
      "Scan Code Repository to compare the current version with an older version of the Code Repository as a baseline, highlighting new vulnerabilities with only High and Critical severities.",
    codeSnippet: `kodem-cli scan code-repository open-source /path/to/<current-repo> \\
      --compare-with /path/to/<compare-repo> \\
      --output-severity high,critical`
  },
  {
    titleLine1:
      "Scan Code Repository against a CI Protection policy configured in Kodem platform by your organization's Kodem Administrator.",
    codeSnippet: `kodem-cli scan code-repository open-source /path/to/<repo> \\
     --code-repository-name myorg/my-project`
  },
  {
    titleLine1: "Scan Code Repository for code weaknesses.",
    codeSnippet: `kodem-cli scan code-repository code /path/to/<repo> `
  },
  {
    titleLine1:
      "Scan Code Repository to compare the current version with an older version of the Code Repository as a baseline, highlighting new code weaknesses with High severities.",
    codeSnippet: `kodem-cli scan code-repository code /path/to/<repo-current> \\
    --compare-with /path/to/<repo-old> \\
    --output-severity high`
  },
  {
    titleLine1: "Scan Container Image for open-source vulnerabilities.",
    codeSnippet: `kodem-cli scan image <image-name:tag>`
  },
  {
    titleLine1:
      "Scan a Container Image to compare current image `image_name:1` with an old image 'image-name:2' highlighting new vulnerabilities with High and Critical severities.",
    codeSnippet: `kodem-cli scan image 'image_name:1' \\
    --compare-with 'image_name:2' \\
    --output-severity high,critical`
  }
]

const kodemCILink = `${API_DOCS}/integrations/integrations-overview-1/configuring-kodem-ci-tool-integration-1`
const kodemContinuousIntegrationLink = `${API_DOCS}/integrations/configuring-kodem-ci-tool-integration`

export const kodemCIIntegrationsStaticInfo: IntegrationStaticInfo[] = [
  {
    name: "kodem_ci",
    displayName: "Kodem CI",
    onlyLogo: true,
    description:
      "Kodem CI is an image-based tool that allows scanning resources on demand",
    tags: [INTEGRATION_TAGS.continuousIntegration],
    connectPage: {
      sections: [
        {
          title: "About",
          description:
            "The Kodem CI Tool enables on-demand scanning of Code Repositories and Container Images for open-source vulnerabilities and code weaknesses. It can be integrated into any CI tool, or run directly on any machine. Kodem CI helps secure your SDLC by enforcing CI policies and allows uploading results to the Kodem platform, for correlation with runtime and improved triage."
        },
        {
          title: "Setup",
          noteTitle: "Note",
          prerequisites: [
            "The Kodem CI image is compatible with Linux and macOS, supporting both ARM64 and amd64 architectures.",
            "Kodem CI image can run in both Docker and Containerd.",
            "You need to be an admin in Kodem in order to generate a service key for the integration."
          ],
          instructionsLink: kodemCILink
        },
        {
          title: "How To Use The Kodem CI Image",
          howToUse: { integrationSample: "TBD" },
          instructionsLink: kodemContinuousIntegrationLink
        }
      ]
    }
  },
  {
    name: "jenkins",
    displayName: "Jenkins",
    onlyLogo: true,
    description:
      "Proactively secure your CI pipelines with early detection of security flaws",
    tags: [INTEGRATION_TAGS.continuousIntegration],
    connectPage: {
      sections: [
        {
          title: "About",
          description:
            "Integrate Kodem's Continuous Integration tool within your Jenkins pipelines to secure your SDLC. Enforce CI policies and upload scan results to the Kodem platform to correlate with runtime and triage with ease."
        },
        {
          title: "Setup",
          noteTitle: "Note",
          prerequisites: [
            "The Kodem CI image is compatible with Linux and macOS, supporting both ARM64 and amd64 architectures.",
            "Kodem CI image can run in both Docker and Containerd.",
            "You need to be an admin in Kodem in order to generate a service key for the integration."
          ],
          instructionsLink: kodemCILink
        },
        {
          title: "How To Use The Kodem CI Image",
          instructionsLink: kodemContinuousIntegrationLink,
          howToUse: {
            integrationSample: `pipeline {
     agent any

     stages {
         stage('Vulnerability Scan') {
             steps {
                 withCredentials([string(credentialsId: 'kodem_service_key', variable: 'KODEM_SERVICE_KEY')]) {
                     sh """
                         docker run --rm \\
                             -v /var/run/docker.sock:/var/run/docker.sock \\
                             -e KODEM_SERVICE_KEY=\${KODEM_SERVICE_KEY} \\
                             ${KodemCIPublicImageName}:${VERSION_PLACEHOLDER} scan image <IMAGE_TO_SCAN> \\
                             -v --output-severity=critical,high
                     """
                 }
             }
         }
     }
 }`
          }
        }
      ]
    }
  },
  {
    name: "github_actions",
    displayName: "GitHub Actions",
    onlyLogo: true,
    description:
      "Proactively secure your CI pipelines with early detection of security flaws",
    tags: [INTEGRATION_TAGS.continuousIntegration],
    connectPage: {
      sections: [
        {
          title: "About",
          description:
            "Integrate Kodem's Continuous Integration tool within your Github Actions pipelines to secure your SDLC. Enforce CI policies and upload scan results to the Kodem platform to correlate with runtime and triage with ease."
        },
        {
          title: "Setup",
          noteTitle: "Note",
          prerequisites: [
            "The Kodem CI image is compatible with Linux and macOS, supporting both ARM64 and amd64 architectures.",
            "Kodem CI image can run in both Docker and Containerd.",
            "You need to be an admin in Kodem in order to generate a service key for the integration."
          ],
          instructionsLink: kodemCILink
        },
        {
          title: "How To Use The Kodem CI Image",
          instructionsLink: kodemContinuousIntegrationLink,
          howToUse: {
            integrationSample: [
              {
                titleLine1:
                  "Scan a Container Image for Critical and High severity open-source vulnerabilities.",
                codeSnippet: `name: CI
on:
  push:
    branches: [ main ]
  pull_request:
    branches: [ main ]

jobs:
  build:
    runs-on: ubuntu-latest
    steps:
      - name: Pull the image
        run: |
          docker pull <IMAGE_TO_SCAN>
          docker pull ${KodemCIPublicImageName}:${VERSION_PLACEHOLDER}

      - name: Run the scan
        run: |
          docker run --rm \\
            -v /var/run/docker.sock:/var/run/docker.sock \\
            -e KODEM_SERVICE_KEY=\${KODEM_SERVICE_KEY} \\
            ${KodemCIPublicImageName}:${VERSION_PLACEHOLDER} scan image <IMAGE_TO_SCAN> \\
            -v --output-severity=critical,high`
              },
              {
                titleLine1:
                  "Scan Code Repository and display only Critical and High severity code weaknesses.",
                codeSnippet: `name: CI
on:
  push:
    branches: [ main ]
  pull_request:
    branches: [ main ]

jobs:
  build:
    runs-on: ubuntu-latest
    steps:
      - uses: actions/checkout@v4
        with:
          fetch-depth: 0
          ref: \${{ github.event.pull_request.head.ref }}
          path: current

      - name: Pull the image
        run: |
          docker pull ${KodemCIPublicImageName}:${VERSION_PLACEHOLDER}

      - name: Run the scan
        run: |
          docker run --rm -t \\
              -v "$(pwd)/current:/tmp/repo" \\
              -e KODEM_SERVICE_KEY="\${{ secrets.KODEM_SERVICE_KEY }}" \\
              ${KodemCIPublicImageName}:${VERSION_PLACEHOLDER} \\
              scan code-repository code /tmp/repo --output-severity=Critical,High`
              },
              {
                titleLine1:
                  "Scan a Code Repository to compare the current version with an older version of the Code Repository as a baseline, highlighting new code weaknesses with Critical and High severities.",
                codeSnippet: `name: CI
on:
  push:
    branches: [ main ]
  pull_request:
    branches: [ main ]

jobs:
  build:
    runs-on: ubuntu-latest
    steps:
      - uses: actions/checkout@v4
        with:
          fetch-depth: 0
          ref: main
          path: main

      - uses: actions/checkout@v4
        with:
          fetch-depth: 0
          ref: \${{ github.event.pull_request.head.ref }}
          path: current

      - name: Pull the image
        run: |
          docker pull ${KodemCIPublicImageName}:${VERSION_PLACEHOLDER}

      - name: Run the scan
        run: |
          docker run --rm -t \\
              -v "$(pwd)/current:/tmp/repo" \\
              -v "$(pwd)/main:/tmp/baseline_repo" \\
              -e KODEM_SERVICE_KEY="\${{ secrets.KODEM_SERVICE_KEY }}" \\
              ${KodemCIPublicImageName}:${VERSION_PLACEHOLDER} \\
              scan code-repository code /tmp/repo --output-severity=Critical,High --compare-with /tmp/baseline_repo`
              }
            ]
          }
        }
      ]
    }
  },
  {
    name: "circle_ci",
    displayName: "Circle CI",
    description:
      "Proactively secure your CI pipelines with early detection of security flaws",
    tags: [INTEGRATION_TAGS.continuousIntegration],
    connectPage: {
      sections: [
        {
          title: "About",
          description:
            "Integrate Kodem's Continuous Integration tool within your CircleCI pipelines to secure your SDLC. Enforce CI policies and upload scan results to the Kodem platform to correlate with runtime and triage with ease."
        },
        {
          title: "Setup",
          prerequisites: [
            "The Kodem CI image is compatible with Linux and macOS, supporting both ARM64 and amd64 architectures.",
            "Kodem CI image can run in both Docker and Containerd.",
            "You need to be an admin in Kodem in order to generate a service key for the integration."
          ],
          instructionsLink: kodemCILink
        },
        {
          title: "How To Use The Kodem CI Image",
          instructionsLink: kodemContinuousIntegrationLink,
          howToUse: {
            integrationSample: [
              {
                titleLine1:
                  "Scan a Container Image for Critical and High severity open-source vulnerabilities.",
                codeSnippet: `version: 2.1

jobs:
  scan-image:
    docker:
      - image: cimg/base:stable
    steps:
      - setup_remote_docker:
          version: 20.10.14
          docker_layer_caching: true

      - run:
          name: Pull images
          command: |
            docker pull <IMAGE_TO_SCAN>
            docker pull ${KodemCIPublicImageName}:${VERSION_PLACEHOLDER}

      - run:
          name: Run Kodem scan
          command: |
            docker run --rm \\
              -v /var/run/docker.sock:/var/run/docker.sock \\
              -e KODEM_SERVICE_KEY=\${KODEM_SERVICE_KEY} \\
              ${KodemCIPublicImageName}:${VERSION_PLACEHOLDER} scan image <IMAGE_TO_SCAN> \\
              -v --output-severity=critical,high

workflows:
  version: 2
  scan:
    jobs:
      - scan-image`
              },
              {
                titleLine1:
                  "Scan Code Repository and display only Critical and High severity code weaknesses.",
                codeSnippet: `version: 2.1

jobs:
  scan-code:
    docker:
      - image: cimg/base:stable
    steps:
      - checkout

      - setup_remote_docker:
          version: 20.10.14
          docker_layer_caching: true

      - run:
          name: Pull Kodem CI image
          command: |
            docker pull ${KodemCIPublicImageName}:${VERSION_PLACEHOLDER}

      - run:
          name: Run Kodem code scan
          command: |
            docker run --rm -t \\
              -v "$(pwd):/tmp/repo" \\
              -e KODEM_SERVICE_KEY="\${KODEM_SERVICE_KEY}" \\
              ${KodemCIPublicImageName}:${VERSION_PLACEHOLDER} \\
              scan code-repository code /tmp/repo --output-severity=Critical,High

workflows:
  version: 2
  scan:
    jobs:
      - scan-code`
              },
              {
                titleLine1:
                  "Scan a Code Repository to compare the current version with an older version of the Code Repository as a baseline, highlighting new code weaknesses with Critical and High severities.",
                codeSnippet: `version: 2.1

jobs:
  scan-code-compare:
    docker:
      - image: cimg/base:stable
    steps:
      - checkout:
          path: current

      - run:
          name: Checkout baseline branch
          command: |
            git clone --single-branch --branch main \${CIRCLE_REPOSITORY_URL} main

      - setup_remote_docker:
          version: 20.10.14
          docker_layer_caching: true

      - run:
          name: Pull Kodem CI image
          command: |
            docker pull ${KodemCIPublicImageName}:${VERSION_PLACEHOLDER}

      - run:
          name: Run Kodem code scan with comparison
          command: |
            docker run --rm -t \\
              -v "$(pwd)/current:/tmp/repo" \\
              -v "$(pwd)/main:/tmp/baseline_repo" \\
              -e KODEM_SERVICE_KEY="\${KODEM_SERVICE_KEY}" \\
              ${KodemCIPublicImageName}:${VERSION_PLACEHOLDER} \\
              scan code-repository code /tmp/repo --output-severity=Critical,High --compare-with /tmp/baseline_repo

workflows:
  version: 2
  scan:
    jobs:
      - scan-code-compare`
              }
            ]
          }
        }
      ]
    }
  }
]

const integrationsStaticInfo: IntegrationStaticInfo[] = [
  {
    name: "azure_repos",
    displayName: "Azure Repos",
    description:
      "Automatically scan and monitor Azure Repos for early vulnerability detection in SDLC",
    tags: [INTEGRATION_TAGS.sourceControl],
    connectPage: {
      sections: [
        {
          title: "About",
          description:
            "Integrate your Azure Repos with Kodem to detect package vulnerabilities, malicious packages, code weaknesses, and exposed secrets across projects. Discovered issues are correlated with runtime images, helping to improve triage and taking action."
        },
        {
          title: "Setup",
          prerequisites: [
            "The user used to activate the Azure Integration must be a Project Administrator in all Projects you wish to integrate with Kodem. ",
            "It is recommend to create a <strong>dedicated user to integrate with Kodem</strong> so you can control the projects that are integrated. The username will show on PR comments.",
            "A PAT (Personal Access Token) is required to complete the integration."
          ],
          instructionsLink: `${API_DOCS}/integrations/azure-repos`
        }
      ],
      submitButton: {
        description: "",
        note: "Configure SCM Policies to enforce Azure Repos status checks",
        connect: "connect",
        label: "Connect Azure Account",
        link: "/integrations/azure_repos",
        disabled: true
      }
    }
  },
  {
    name: "bitbucket",
    displayName: "Bitbucket Cloud",
    description:
      "Automatically scan and monitor Bitbucket Cloud for early vulnerability detection in SDLC",
    tags: [INTEGRATION_TAGS.sourceControl],
    connectPage: {
      sections: [
        {
          title: "About",
          description:
            "Integrate your Bitbucket workspace with Kodem to detect package vulnerabilities, malicious packages, code weaknesses and exposed secrets across code repositories. Discovered issues are correlated to runtime images helping better triage."
        },
        {
          title: "Setup",
          prerequisites: [
            "The user used to activate the Bitbucket Integration must be a Workspace Admin in all workspaces you wish to integrate with Kodem."
          ],
          instructionsLink: `${API_DOCS}/integrations/bitbucket-cloud`
        },
        {
          title: "How To Install the Kodem Bitbucket Integration",
          installSteps: [
            {
              title: "Open the Kodem Integration for Bitbucket Cloud app",
              button: {
                label: "Open Bitbucket",
                link: "https://developer.atlassian.com/console/install/bc383c8a-3ba6-48b1-b2b2-b382a52287c4?signature=AYABeARKXSIw27g4ywpNzgPbwwgAAAADAAdhd3Mta21zAEthcm46YXdzOmttczp1cy13ZXN0LTI6NzA5NTg3ODM1MjQzOmtleS83MDVlZDY3MC1mNTdjLTQxYjUtOWY5Yi1lM2YyZGNjMTQ2ZTcAuAECAQB4IOp8r3eKNYw8z2v%2FEq3%2FfvrZguoGsXpNSaDveR%2FF%2Fo0Bm1Q0OjcgTdlDkVrzMw7pBwAAAH4wfAYJKoZIhvcNAQcGoG8wbQIBADBoBgkqhkiG9w0BBwEwHgYJYIZIAWUDBAEuMBEEDM4cRs0mf9m7d%2FrRCwIBEIA7IBBnX6zkQbyMwAnkdGBlJXf1cbC6XKVpUl1KOIakjrZ8EdNekOAE7v8IS9b0N4CIWmYi1T3UlpWCTFMAB2F3cy1rbXMAS2Fybjphd3M6a21zOmV1LXdlc3QtMTo3MDk1ODc4MzUyNDM6a2V5LzQ2MzBjZTZiLTAwYzMtNGRlMi04NzdiLTYyN2UyMDYwZTVjYwC4AQICAHijmwVTMt6Oj3F%2B0%2B0cVrojrS8yZ9ktpdfDxqPMSIkvHAHD4uVQFwAGbxvNSjChbOqmAAAAfjB8BgkqhkiG9w0BBwagbzBtAgEAMGgGCSqGSIb3DQEHATAeBglghkgBZQMEAS4wEQQMxKQtjSfd4zIXoS3pAgEQgDsb%2Fwcz3Qa6xC2wYYCwGpJVkvHEE87e3q4ZdVgcARewvSm%2BuuPlFZaGDcDpBwRlNSGGPq%2FI9LD0v8paMQAHYXdzLWttcwBLYXJuOmF3czprbXM6dXMtZWFzdC0xOjcwOTU4NzgzNTI0MzprZXkvNmMxMjBiYTAtNGNkNS00OTg1LWI4MmUtNDBhMDQ5NTJjYzU3ALgBAgIAeLKa7Dfn9BgbXaQmJGrkKztjV4vrreTkqr7wGwhqIYs5AY3c13g2HedxG0Uv4P0j4mgAAAB%2BMHwGCSqGSIb3DQEHBqBvMG0CAQAwaAYJKoZIhvcNAQcBMB4GCWCGSAFlAwQBLjARBAy5N2TEXlI3UG6CYhgCARCAO61sbgP40Z65p1s96LJNVG4QnVy6kK%2FDwrP7h8t1tjugDQ07BzDn23Du5s4TgPlusNKQiGj3uPLJp8pwAgAAAAAMAAAQAAAAAAAAAAAAAAAAAJ%2B2yr3PtAjpsNeEsxI3O5j%2F%2F%2F%2F%2FAAAAAQAAAAAAAAAAAAAAAQAAADKRqe44cY63c7axggw%2BV2RJKmVT8aOWECZ7maoOWZCHH9eveiHD0k5d%2FD%2B50mt4kjmnqElxAkNgY4FP32RGEz%2FLiRY%3D&product=bitbucket",
                alert: "integrations.connect.bitbucket.buttonAlert",
                anchorId: "part-pairing"
              }
            },
            {
              title: 'Select "Get app"'
            },
            {
              title:
                'Choose the Bitbucket workspace you want to connect and hit "Grant access"'
            },
            {
              anchorId: "part-pairing",
              title: "Pair the app with Kodem (required to finish)",
              description: "Pair the integration in Bitbucket:",
              subSteps: [
                "In Bitbucket, select Settings and open Workspace settings.",
                "In the left sidebar, select Forge Apps.",
                "Open Kodem Integration for Bitbucket Cloud.",
                "Select Pair with Kodem to connect the integration to your Kodem tenant."
              ]
            }
          ],
          importantNote:
            "Important - If a legacy Connect app is installed, remove it and keep only the Forge app."
        }
      ],
      submitButton: {
        description:
          "Integrate Kodem with Bitbucket using the Atlassian Marketplace:",
        label: "Open Atlassian Marketplace",
        note: "Configure SCM Policies to enforce Bitbucket build checks",
        link: "https://developer.atlassian.com/console/install/bc383c8a-3ba6-48b1-b2b2-b382a52287c4?signature=AYABeARKXSIw27g4ywpNzgPbwwgAAAADAAdhd3Mta21zAEthcm46YXdzOmttczp1cy13ZXN0LTI6NzA5NTg3ODM1MjQzOmtleS83MDVlZDY3MC1mNTdjLTQxYjUtOWY5Yi1lM2YyZGNjMTQ2ZTcAuAECAQB4IOp8r3eKNYw8z2v%2FEq3%2FfvrZguoGsXpNSaDveR%2FF%2Fo0Bm1Q0OjcgTdlDkVrzMw7pBwAAAH4wfAYJKoZIhvcNAQcGoG8wbQIBADBoBgkqhkiG9w0BBwEwHgYJYIZIAWUDBAEuMBEEDM4cRs0mf9m7d%2FrRCwIBEIA7IBBnX6zkQbyMwAnkdGBlJXf1cbC6XKVpUl1KOIakjrZ8EdNekOAE7v8IS9b0N4CIWmYi1T3UlpWCTFMAB2F3cy1rbXMAS2Fybjphd3M6a21zOmV1LXdlc3QtMTo3MDk1ODc4MzUyNDM6a2V5LzQ2MzBjZTZiLTAwYzMtNGRlMi04NzdiLTYyN2UyMDYwZTVjYwC4AQICAHijmwVTMt6Oj3F%2B0%2B0cVrojrS8yZ9ktpdfDxqPMSIkvHAHD4uVQFwAGbxvNSjChbOqmAAAAfjB8BgkqhkiG9w0BBwagbzBtAgEAMGgGCSqGSIb3DQEHATAeBglghkgBZQMEAS4wEQQMxKQtjSfd4zIXoS3pAgEQgDsb%2Fwcz3Qa6xC2wYYCwGpJVkvHEE87e3q4ZdVgcARewvSm%2BuuPlFZaGDcDpBwRlNSGGPq%2FI9LD0v8paMQAHYXdzLWttcwBLYXJuOmF3czprbXM6dXMtZWFzdC0xOjcwOTU4NzgzNTI0MzprZXkvNmMxMjBiYTAtNGNkNS00OTg1LWI4MmUtNDBhMDQ5NTJjYzU3ALgBAgIAeLKa7Dfn9BgbXaQmJGrkKztjV4vrreTkqr7wGwhqIYs5AY3c13g2HedxG0Uv4P0j4mgAAAB%2BMHwGCSqGSIb3DQEHBqBvMG0CAQAwaAYJKoZIhvcNAQcBMB4GCWCGSAFlAwQBLjARBAy5N2TEXlI3UG6CYhgCARCAO61sbgP40Z65p1s96LJNVG4QnVy6kK%2FDwrP7h8t1tjugDQ07BzDn23Du5s4TgPlusNKQiGj3uPLJp8pwAgAAAAAMAAAQAAAAAAAAAAAAAAAAAJ%2B2yr3PtAjpsNeEsxI3O5j%2F%2F%2F%2F%2FAAAAAQAAAAAAAAAAAAAAAQAAADKRqe44cY63c7axggw%2BV2RJKmVT8aOWECZ7maoOWZCHH9eveiHD0k5d%2FD%2B50mt4kjmnqElxAkNgY4FP32RGEz%2FLiRY%3D&product=bitbucket"
      }
    }
  },
  {
    name: "github",
    displayName: "GitHub",
    description:
      "Automatically scan and monitor GitHub for early vulnerability detection in SDLC",
    tags: [INTEGRATION_TAGS.sourceControl],
    connectPage: {
      sections: [
        {
          title: "About",
          description:
            "Integrate your GitHub organizations with Kodem to detect package vulnerabilities, malicious packages, code weaknesses and exposed secrets across code repositories. Discovered issues are correlated to runtime images helping better triage and allowing for secured SCM policies enforcement."
        },
        {
          title: "Setup",
          prerequisites: [
            {
              "The user activating the Kodem application requires the following permissions on the desired GitHub organizations:":
                [
                  "Read access to code.",
                  "Read access to metadata.",
                  "Read permissions for packages.",
                  "Read and write access to pull requests metadata."
                ]
            }
          ],
          instructionsLink: `${API_DOCS}/integrations/github`
        }
      ],
      submitButton: {
        description: "Install the Kodem App from the GitHub Marketplace:",
        note: "Configure SCM Policies to enforce GitHub security checks",
        label: "Open GitHub Marketplace",
        link: "https://github.com/apps/kodem-security"
      }
    }
  },
  {
    name: "gitlab",
    displayName: "GitLab",
    description:
      "Automatically scan and monitor GitLab for early vulnerability detection in SDLC",
    tags: [INTEGRATION_TAGS.sourceControl],
    connectPage: {
      sections: [
        {
          title: "About",
          description:
            "Integrate your GitLab Groups with Kodem to detect package vulnerabilities, malicious packages, code weaknesses and exposed secrets across projects. Discovered issues are correlated with runtime images, helping to improve triage and taking action."
        },
        {
          title: "Setup",
          prerequisites: [
            "The user activating the GitLab Integration must be a GitLab Owner or Maintainer in order to integrate the GitLab Account with Kodem."
          ],
          instructionsLink: `${API_DOCS}/integrations/gitlab`
        }
      ],
      submitButton: {
        description: "",
        note: "Configure SCM Policies to enforce GitLab status checks",
        connect: "connect",
        label: "Connect GitLab Account",
        link: "/integrations/gitlab",
        disabled: true
      }
    }
  },
  {
    name: INTEGRATION_VENDORS.packageRegistry,
    displayName: "Private Package Registry",
    description:
      "Connect your private registries so Kodem can resolve, fix and validate private dependencies.",
    tags: ["Package registry"],
    connectPage: {
      sections: [
        {
          title: "About",
          description:
            "Kodem builds your dependency tree by running your package managers. When a dependency lives in a private registry, Kodem needs read access. Connecting a registry unlocks the full hierarchy for npm, Maven, Gradle and Python projects, fix generation for private packages, and exploit validation through them."
        },
        {
          title: "Prerequisites",
          description: "To connect a registry, have the following ready:",
          prerequisites: [
            "The registry endpoint your developers use, one per package format, for example https://acme.example.com/artifactory/api/npm/npm-virt or https://maven.pkg.github.com/acme-corp",
            "A read-only credential that endpoint accepts: a token, or a username and password. Artifactory: identity or access token. Nexus: user and password, and an npm token for npm. GitHub: a PAT with read:packages, SSO-authorized.",
            "Maven and Gradle: only the credential. Their repositories come from your pom.xml and build.gradle."
          ]
        }
      ],
      submitButton: {
        description: "",
        note: "",
        connect: "connect",
        label: "Add Registry",
        link: "/integrations/package_registry",
        disabled: true
      }
    }
  },
  {
    name: INTEGRATION_VENDORS.acr,
    displayName: "Azure Container Registry",
    description:
      "Connect Azure Container Registry to Kodem for private image scans and remediation",
    tags: ["Container registry"],
    connectPage: {
      sections: [
        {
          title: "About",
          description:
            "Integrating Azure Container Registry (ACR) with Kodem provides full image visibility, enabling private base image scanning and remediation, supporting on-demand registry scanning, and improving overall image scan efficiency."
        },
        {
          title: "Prerequisites",
          description:
            "To connect Azure Container Registry, have the following ready:",
          prerequisites: [
            "Permission to create a managed identity and to assign roles on the registries: Owner, or Contributor with User Access Administrator.",
            "The registries' login servers, and optionally their Base Image Repositories. Kodem reads them as a managed identity you create, which trusts Kodem through a federated credential: no secret is shared, and you can remove it at any time."
          ],
          instructionsLink: `${API_DOCS}/integrations/azure-container-registry`
        }
      ],
      submitButton: {
        description: "",
        note: "",
        connect: "connect",
        label: "Connect Azure Container Registry",
        link: "/integrations/acr",
        disabled: true
      }
    }
  },
  {
    name: "ecr",
    displayName: "Amazon ECR",
    description: "Connect ECR to Kodem for private image scans and remediation",
    tags: ["Container registry"],
    connectPage: {
      sections: [
        {
          title: "About",
          description:
            "Integrating AWS Elastic Container Registry (ECR) with Kodem provides full image visibility, enabling private base image scanning and remediation, supporting on-demand registry scanning, and improving overall image scan efficiency."
        },
        {
          title: "Setup",
          prerequisites: [
            "You need to be an admin in your AWS account in order to integrate your AWS ECR with Kodem."
          ],
          instructionsLink: `${API_DOCS}/integrations/amazon-ecr`
        }
      ],
      submitButton: {
        description: "",
        label: "Connect AWS Account",
        connect: "connect",
        link: `${API_DOCS}/integrations/amazon-ecr`
      }
    }
  },
  {
    name: INTEGRATION_VENDORS.dockerHub,
    displayName: "Docker Hub",
    description:
      "Connect Docker Hub to Kodem for private image scans and remediation",
    tags: ["Container registry"],
    connectPage: {
      sections: [
        {
          title: "About",
          description:
            "Integrating Docker Hub container image registry with Kodem provides full image visibility, enabling private base image scanning and remediation, supporting on-demand registry scanning, and improving overall image scan efficiency."
        },
        {
          title: "Prerequisites",
          description: "To connect Docker Hub, have the following ready:",
          prerequisites: [
            "A read-only access token: a personal access token with your username, or an organization access token with your organization name. An organization access token needs these scopes on the repositories Kodem reads, your base image repositories and the private repositories of the images you deploy: Image Pull, to pull images; Repository Read, to check that each repository is private; and Tag Read, to list new tags.",
            "Optionally, the private base image repositories to scan, as namespace/name, for example acme/api. Public repositories and official images are scanned without credentials and can't be added."
          ],
          instructionsLink: `${API_DOCS}/integrations/docker-hub`
        }
      ],
      submitButton: {
        description: "",
        note: "",
        connect: "connect",
        label: "Connect Docker Hub",
        link: "/integrations/docker_hub",
        disabled: true
      }
    }
  },
  {
    name: "gcr",
    displayName: "Google Artifact Registry",
    description:
      "Connect Google Artifact Registry to Kodem for private image scans and remediation",
    tags: ["Container registry"],
    connectPage: {
      sections: [
        {
          title: "About",
          description:
            "Integrating Google Artifact Registry (GAR) with Kodem provides full image visibility, enabling private base image scanning and remediation, supporting on-demand registry scanning, and improving overall image scan efficiency."
        },
        {
          title: "Prerequisites",
          description:
            "To connect Artifact Registry, have the following ready:",
          prerequisites: [
            "Permission to grant roles: Artifact Registry Administrator on each repository to grant it per repository, or Owner or Project IAM Admin on the project to grant it on the whole project.",
            "The Artifact Registry locations to connect, and optionally their Base Image Repositories as project/repository/image. Kodem gives you a service account of your own; you grant it Artifact Registry Reader, and can revoke it at any time."
          ],
          instructionsLink: `${API_DOCS}/integrations/google-artifact-registry`
        }
      ],
      submitButton: {
        description: "",
        note: "",
        connect: "connect",
        label: "Connect Artifact Registry",
        link: "/integrations/gcr",
        disabled: true
      }
    }
  },
  {
    name: INTEGRATION_VENDORS.webhook,
    displayName: "Webhook Endpoints",
    description: "Trigger Webhook Endpoints to monitor Kodem findings",
    tags: ["Notifications"],
    connectPage: {
      sections: [
        {
          title: "About",
          description:
            "Leverage Webhook Endpoints to automate actions based on Kodem system events. By configuring Webhooks, you can receive real-time updates and seamlessly integrate with services like notification and incident response systems. To trigger a webhook, set up an automated action with a webhook."
        },
        {
          title: "Setup",
          description:
            "To set up webhook endpoints, ensure you have the following permissions:",
          prerequisites: [
            "Access to the desired endpoint URL.",
            "Secret (optional)."
          ]
        }
      ],
      submitButton: {
        description: "",
        note: "",
        connect: "connect",
        label: "Add Endpoint",
        link: "/integrations/webhook",
        disabled: true
      }
    }
  },
  {
    name: INTEGRATION_VENDORS.asana,
    displayName: "Asana",
    description:
      "Streamline your remediation by creating Asana tasks for Kodem findings",
    tags: ["Ticketing"],
    connectPage: {
      sections: [
        {
          title: "About",
          description:
            "Create Asana tasks for Kodem findings with full applicative context, by hand or from automated workflows, and keep them updated as the findings change."
        },
        {
          title: "Setup",
          prerequisites: [
            "The Asana user who connects Kodem must be a member of the workspace and able to create and edit tasks in the projects Kodem should create tasks in.",
            "It is recommended to connect with a <strong>dedicated Asana user for Kodem</strong>. Its name will show on the tasks and comments Kodem creates."
          ],
          instructionsLink: `${API_DOCS}/integrations/asana`
        }
      ],
      submitButton: {
        description: "",
        note: "",
        connect: "connect",
        label: "Connect Asana",
        link: `/integrations/${INTEGRATION_VENDORS.asana}`
      }
    }
  },
  {
    name: INTEGRATION_VENDORS.azureBoards,
    displayName: "Azure Boards",
    description:
      "Streamline your remediation by creating Azure Boards work items for Kodem findings",
    tags: ["Ticketing"],
    connectPage: {
      sections: [
        {
          title: "About",
          description:
            "Create Azure Boards work items for Kodem findings with full applicative context, by hand or from automated workflows, and keep them updated as the findings change."
        },
        {
          title: "Setup",
          prerequisites: [
            "The user used to activate the Azure Boards Integration must be able to create and edit work items in the projects Kodem should create work items in.",
            "It is recommended to create a <strong>dedicated user to integrate with Kodem</strong>. The username will show on the work items and comments Kodem creates.",
            "The name of your Azure DevOps organization.",
            "A PAT (Personal Access Token) with the Work Items (Read & write) and Project and Team (Read) scopes is required to complete the integration. Kodem never shows the token again."
          ],
          instructionsLink: `${API_DOCS}/integrations/azure-boards`
        }
      ],
      submitButton: {
        description: "",
        note: "",
        connect: "connect",
        label: "Connect Azure Boards",
        link: `/integrations/${INTEGRATION_VENDORS.azureBoards}`
      }
    }
  },
  {
    name: "jira",
    displayName: "Jira Cloud",
    description:
      "Streamline your remediation by creating a Jira issue for Kodem findings",
    tags: ["Ticketing"],
    connectPage: {
      sections: [
        {
          title: "About",
          description:
            "Streamline your remediation process by creating a Jira issue for Kodem findings. You can triage effortlessly by creating Jira issues with full applicative context and sending them directly to the issue owner."
        },
        {
          title: "Setup",
          prerequisites: [
            "The user activating the integration must be a Jira System administrator."
          ],
          instructionsLink: `${API_DOCS}/integrations/jira-cloud`
        }
      ],
      submitButton: {
        description:
          "Integrate Kodem with Jira Cloud using the Atlassian Marketplace:",
        label: "Open Atlassian Marketplace",
        link: "https://marketplace.atlassian.com/apps/1229653?tab=overview&hosting=cloud"
      }
    }
  },
  {
    name: "wiz",
    displayName: "Wiz",
    description:
      "Complement your cloud security findings with runtime intelligence",
    tags: ["CSPM"],
    connectPage: {
      sections: [
        {
          title: "About",
          description:
            "Use Kodem's integration with Wiz to complement your Cloud security findings with runtime intelligence. Kodem provides comprehensive applicative-level insights, helping you understand the real impact of your CSPM findings."
        },
        {
          title: "Setup",
          prerequisites: [
            "The activating user must have one of the following roles in the Wiz platform: Project Admin, Global Contributor or Global Admin."
          ]
        }
      ],
      submitButton: {
        description: "",
        label: "Setup instructions",
        link: `${PUBLIC_DOCS}/integrations/Wiz_integration_for_Kodem.pdf`
      }
    }
  },
  {
    name: KODEM_MCP_INTEGRATION_NAME,
    displayName: "Kodem MCP Server",
    description:
      "Bring your Kodem issues, inventory and runtime context into your AI assistant",
    tags: [INTEGRATION_TAGS.aiAgents],
    onlyLogo: true,
    connectPage: {
      sections: [
        {
          title: "About",
          description:
            "The Kodem MCP server gives AI assistants such as Claude, Cursor and GitHub Copilot access to your Kodem data: issues with their runtime context, inventory, deployments, scan history and posture metrics. Ask in plain language, and the answers can weigh what actually runs, not severity alone. The assistant works with your own permissions and scopes."
        },
        {
          title: "Setup",
          prerequisites: [
            "An MCP client with OAuth sign-in, such as Claude Code, Claude, Cursor or GitHub Copilot."
          ]
        },
        { title: "How To Connect the Kodem MCP Server" }
      ]
    }
  },
  codingAgentIntegration({
    name: ANTIGRAVITY_INTEGRATION_NAME,
    displayName: "Antigravity",
    prerequisites: [
      "Antigravity CLI installed on the developer's machine.",
      "git on the developer's machine."
    ],
    installTitle: "How To Install the Kodem Antigravity Integration"
  }),
  codingAgentIntegration({
    name: CLAUDE_CODE_INTEGRATION_NAME,
    displayName: "Claude Code",
    prerequisites: ["Claude Code installed on the developer's machine."],
    installTitle: "How To Install the Kodem Claude Integration",
    instructionsLink: `${API_DOCS}/integrations/kodem-security-skills-for-claude-code`
  }),
  codingAgentIntegration({
    name: CODEX_INTEGRATION_NAME,
    displayName: "Codex",
    prerequisites: ["Codex CLI installed on the developer's machine."],
    installTitle: "How To Install the Kodem Codex Integration"
  }),
  codingAgentIntegration({
    name: CURSOR_INTEGRATION_NAME,
    displayName: "Cursor",
    prerequisites: ["Cursor installed on the developer's machine."],
    installTitle: "How To Install the Kodem Cursor Integration"
  }),
  codingAgentIntegration({
    name: GEMINI_CLI_INTEGRATION_NAME,
    displayName: "Gemini CLI",
    prerequisites: [
      "Gemini CLI installed on the developer's machine, signed in with a Gemini Code Assist Standard or Enterprise license or a paid Gemini API key.",
      "git on the developer's machine."
    ],
    installTitle: "How To Install the Kodem Gemini CLI Integration"
  }),
  codingAgentIntegration({
    name: GITHUB_COPILOT_INTEGRATION_NAME,
    displayName: "GitHub Copilot",
    prerequisites: ["GitHub Copilot CLI installed on the developer's machine."],
    installTitle: "How To Install the Kodem GitHub Copilot Integration"
  }),
  {
    name: "jetbrains",
    displayName: "JetBrains",
    description:
      "Scan code directly in your development environment to identify security findings",
    tags: ["IDE"],
    onlyLogo: true,
    connectPage: {
      sections: [
        {
          title: "About",
          description:
            "Kodem’s Plugin for JetBrains IDEs (IntelliJ IDEA, PyCharm, WebStorm, GoLand, Rider and more) enables the scanning of code directly in your development environment.<br/> Secure your code as you write with on-demand and real-time scanning along with actionable findings displayed in your JetBrains IDE."
        },
        {
          title: "Setup",
          prerequisites: [
            "Supports Linux and macOS (ARM64 / AMD64) and Windows (x86_64 / AMD64).",
            "JetBrains IDEs v2025.1 or later."
          ],
          instructionsLink:
            "https://docs.kodemsecurity.com/integrations/kodem-plugin-for-jetbrains-ides"
        }
      ],
      submitButton: {
        description: "Connect Kodem's JetBrains plugin:",
        label: "Open JetBrains Marketplace",
        link: "https://plugins.jetbrains.com/plugin/34692-kodem",
        icon: "externalLink"
      }
    }
  },
  {
    name: "visualstudio",
    displayName: "Visual Studio",
    description:
      "Scan code directly in your development environment to identify security findings",
    tags: ["IDE"],
    onlyLogo: true,
    connectPage: {
      sections: [
        {
          title: "About",
          description:
            "Kodem’s Extension for Visual Studio enables the scanning of code directly in your development environment.<br/> Secure your code as you write with on-demand and real-time scanning along with actionable findings displayed in Visual Studio."
        },
        {
          title: "Setup",
          prerequisites: [
            "Supports Windows (x86_64 / AMD64 and ARM64).",
            "Visual Studio 2022 v17.14 or later, or Visual Studio 2026."
          ],
          instructionsLink:
            "https://docs.kodemsecurity.com/integrations/kodem-extension-for-visual-studio"
        }
      ],
      submitButton: {
        description: "Connect Kodem's Visual Studio extension:",
        label: "Open Visual Studio Marketplace",
        link: "https://marketplace.visualstudio.com/items?itemName=kodemsecurity.kodem-visualstudio-extension",
        icon: "externalLink"
      }
    }
  },
  {
    name: "vscode",
    displayName: "Visual Studio Code",
    description:
      "Scan code directly in your development environment to identify security findings",
    tags: ["IDE"],
    onlyLogo: true,
    connectPage: {
      sections: [
        {
          title: "About",
          description:
            "Kodem’s Extension for Visual Studio Code enables the scanning of code directly in your development environment.<br/> Secure your code as you write with on-demand and real-time scanning along with actionable findings displayed in Visual Studio Code."
        },
        {
          title: "Setup",
          prerequisites: [
            "Supports Linux and macOS (ARM64 / AMD64) and Windows (x86_64 / AMD64).",
            "Visual Studio Code v1.90.0 or later."
          ],
          instructionsLink:
            "https://docs.kodemsecurity.com/integrations/kodem-extension-for-visual-studio-code"
        }
      ],
      submitButton: {
        description: "Connect Kodem's Visual Studio Code extension:",
        label: "Open Visual Studio Marketplace",
        link: "https://marketplace.visualstudio.com/items?itemName=KodemSecurity.kodem-vscode-extension",
        icon: "externalLink"
      }
    }
  },
  {
    name: "kodem_cli",
    displayName: "Kodem CLI",
    description:
      "A command-line interface that allows scan for issues locally on your machine.",
    tags: ["Command Line"],
    onlyLogo: true,
    connectPage: {
      sections: [
        {
          title: "About",
          description:
            "Kodem CLI is a command-line interface that allows you to scan code repositories and container images for issues locally on your machine."
        },
        {
          title: "Setup",
          prerequisites: [
            "The Kodem CLI is compatible with macOS, Linux and Windows.",
            "Opengrep installation is required to perform a code scan to identify exposed secrets and code weaknesses.",
            "CI Protection and Suppression policies configured in Kodem will be validated when matching repositories are scanned."
          ],
          instructionsLink: `${API_DOCS}/integrations/kodem-cli`
        },
        {
          title: "How to Use the Kodem CLI"
        }
      ]
    }
  },
  ...kodemCIIntegrationsStaticInfo,
  {
    name: INTEGRATION_VENDORS.slack,
    displayName: "Slack",
    description:
      "Be the first to know about security risks with real-time Kodem findings notifications",
    tags: ["Collaboration"],
    connectPage: {
      sections: [
        {
          title: "About",
          description:
            "Post Kodem findings to your Slack channels as they happen. Add a channel with its incoming webhook URL, then set up an automated workflow that sends a Slack message for code issues, open-source issues or incidents, with a message you write from Kodem's dynamic tags."
        },
        {
          title: "Prerequisites",
          description: "To add a Slack channel, have the following ready:",
          prerequisites: [
            "A Slack app with incoming webhooks turned on, installed in your workspace (api.slack.com/apps).",
            "An incoming webhook URL for each channel Kodem should post to. Treat it as a secret: Kodem never shows it again in full."
          ],
          instructionsLink: `${API_DOCS}/integrations/slack`
        }
      ],
      submitButton: {
        description: "",
        note: "",
        connect: "connect",
        label: "Add Channel",
        link: "/integrations/slack",
        disabled: true
      }
    }
  },
  {
    name: "GitLabSourceCode",
    displayName: "GitLab",
    description:
      "Automatically scan and monitor GitLab for early vulnerability detection in SDLC",
    tags: [INTEGRATION_TAGS.sourceControl]
  },
  {
    name: "ServiceNow",
    displayName: "ServiceNow",
    description:
      "Streamline your remediation by creating ServiceNow tickets for Kodem findings",
    tags: ["Ticketing"]
  },
  {
    name: "Splunk",
    displayName: "Splunk",
    description: "Monitor Kodem findings, and Top Alerts within your Splunk",
    tags: ["SIEM"]
  }
]

export const getIntegrationsListData = (response: IntegrationsListResponse) => {
  const { data = [] } = response || {}
  const integrationsStatus: Record<string, boolean> = {}

  const servicesStateByName = data.reduce(
    (acc, integration) => {
      const { name, isEnabled, isInstalled } = integration

      acc[integration.name] = integration

      integrationsStatus[name] = isEnabled && isInstalled

      return acc
    },
    {} as Record<string, Integration>
  )

  const selfHosted = isSelfHosted()
  const merged = integrationsStaticInfo
    .filter((integration) => {
      if (selfHosted) {
        return (
          kodemCIIntegrations.includes(integration.name) ||
          integration.name === "github" ||
          integration.name === "gitlab" ||
          ideIntegrations.includes(integration.name) ||
          isCodingAgentIntegration(integration) ||
          integration.name === KODEM_MCP_INTEGRATION_NAME
        )
      }

      return servicesStateByName[integration.name]
    })
    .map((integration) => ({
      ...integration,
      ...servicesStateByName[integration.name]
    }))
  const metadata: JiraMetadata = { integrationsStatus }
  return { data: merged, metadata }
}

export const getTicketingSystemsData = (response: IntegrationsListResponse) =>
  getConnectedTicketingSystems(
    getIntegrationsListData(response).metadata.integrationsStatus
  )

export const getIntegrationsHealthData = (
  response: IntegrationsListResponse
) => {
  const { data = [] } = response || {}
  const healthByName: Record<string, InstallationHealth> = {}

  data.forEach(({ name, health }) => {
    if (health) healthByName[name] = health
  })

  return healthByName
}

const isGitHubInstallation = (value: unknown): value is GitHubInstallationDTO =>
  isRecord(value) &&
  typeof value.installationId === "number" &&
  typeof value.organizationName === "string"

// Every setting was on before it became configurable
const toIssueNotificationSettings = (
  settings: Partial<IssueNotificationSettings> = {}
): IssueNotificationSettings => ({
  commentOnResolved: settings.commentOnResolved ?? true,
  commentOnDismissed: settings.commentOnDismissed ?? true,
  commentOnRiskChange: settings.commentOnRiskChange ?? true,
  labelOnResolved: settings.labelOnResolved ?? true,
  labelOnDismissed: settings.labelOnDismissed ?? true,
  // GitHub has no Back to Open setting yet
  commentOnUndismissed: settings.commentOnUndismissed ?? false,
  labelOnUndismissed: settings.labelOnUndismissed ?? false
})

export const getGitHubInstallations = (
  configuration: unknown
): GitHubInstallation[] => {
  const installations: unknown[] =
    isRecord(configuration) && Array.isArray(configuration.installations)
      ? configuration.installations
      : []

  return installations
    .filter(isGitHubInstallation)
    .map(
      ({
        issuesEnabled = true,
        issueNotificationSettings,
        issuesPermission = "UNSPECIFIED",
        ...installation
      }) => ({
        ...installation,
        issuesEnabled,
        issueNotificationSettings: toIssueNotificationSettings(
          issueNotificationSettings
        ),
        issuesPermission
      })
    )
}

// Issue creation is allowed while one installation allows it (switch on and the Issues
// permission not missing); disabledOwners are the lowercase owners whose every
// installation doesn't
export const getGitHubIssuesSettingsData = (
  response: IntegrationDetailsResponse
) => {
  const enabledByOwner = new Map<string, boolean>()

  getGitHubInstallations(response?.data?.configuration).forEach(
    (installation) => {
      const owner = installation.organizationName.toLowerCase()
      enabledByOwner.set(
        owner,
        !!enabledByOwner.get(owner) || allowsGitHubIssues(installation)
      )
    }
  )

  const owners = [...enabledByOwner]

  return {
    isAllowed: owners.some(([, enabled]) => enabled),
    disabledOwners: owners
      .filter(([, enabled]) => !enabled)
      .map(([owner]) => owner)
  }
}

export const getIntegrationDetailsData = (
  response: IntegrationDetailsResponse
) => {
  const { data, ...rest } = response || {}
  const {
    integration,
    extraInfo,
    configuration = {} as IntegrationConfiguration
  } = data
  // TODO: api sometimes does not return a name, fix it :)
  const n = integration.name
  const staticInfo = integrationsStaticInfo.find((i) => i.name === n)

  if (!staticInfo) {
    return null
  }

  const selfHosted = isSelfHosted()
  const version = extraInfo?.version
  const toSnippet = (snippet: string) =>
    replaceVersionPlaceholder(
      selfHosted && kodemCIIntegrations.includes(n)
        ? toSelfHostedCISnippet(snippet)
        : snippet,
      version
    )
  // Built per response: the static info is shared, and keeps its placeholders.
  const sections = staticInfo.connectPage?.sections?.map((section) => {
    const sample = section.howToUse?.integrationSample
    return {
      ...section,
      ...(selfHosted && section.instructionsLink && { instructionsLink: "" }),
      ...(sample && {
        howToUse: {
          ...section.howToUse,
          integrationSample: Array.isArray(sample)
            ? sample.map((example) => ({
                ...example,
                codeSnippet: toSnippet(example.codeSnippet)
              }))
            : toSnippet(sample)
        }
      })
    }
  })
  const connectPage: IntegrationStaticInfo["connectPage"] =
    staticInfo.connectPage && {
      ...staticInfo.connectPage,
      sections
    }

  return {
    data: {
      integration: {
        ...staticInfo,
        connectPage,
        ...integration,
        health: getWorstHealth(getInstallationHealths(configuration)),
        isKodemCIIntegration: kodemCIIntegrations.includes(n),
        isKodemCLIIntegration: kodemCLIIntegrations.includes(n),
        isKodemMCPIntegration: n === KODEM_MCP_INTEGRATION_NAME,
        codingAgent: isCodingAgentIntegration(staticInfo) ? n : undefined
      },
      configurationExist: !!data.configuration,
      configuration,
      extraInfo
    },
    ...rest
  }
}

const getInstallationHealths = (
  configuration: IntegrationConfiguration | null
) => {
  if (!configuration) return []
  // Azure Boards and Asana describe one connection, whose health is the configuration's
  const connectionHealth = toInstallationHealth(
    (configuration as Record<string, unknown>).health
  )
  if (connectionHealth) return [connectionHealth]

  const installations =
    "accountRegistries" in configuration
      ? configuration.accountRegistries
      : configuration.installations

  return (installations || []).map((installation) =>
    "health" in installation ? installation.health : undefined
  )
}

const replaceVersionPlaceholder = (
  original: string,
  version: string | undefined
) => {
  return original.replace(
    new RegExp(VERSION_PLACEHOLDER, "g"),
    version || "latest"
  )
}

// A self-hosted pipeline pulls the image from the customer's own registry and uploads to
// their integrations gateway, as the Kodem CI examples do (getKodemCICommonExamples).
const toSelfHostedCISnippet = (snippet: string) =>
  snippet
    .split(`${KodemCIPublicImageName}:${VERSION_PLACEHOLDER}`)
    .join("kodem-image:tag")
    .replace(
      /^(\s*-e )KODEM_SERVICE_KEY=(.*)KODEM_SERVICE_KEY(.*)$/gm,
      (line: string, flag: string, before: string, after: string) =>
        [
          line,
          `${flag}KODEM_INTEGRATIONS_ENDPOINT=${before}KODEM_INTEGRATIONS_ENDPOINT${after}`,
          `${flag}KODEM_DISABLE_RESOURCE_UPDATES=true \\`
        ].join("\n")
    )

export const getPreInstallOptionsECR = (
  response: PreInstallIntegrationResponse
) => {
  const { data } = response || {}
  const { regions = [] } = data || []

  return {
    ...data,
    regionOptions: regions.map((value: string) => ({ value, label: value }))
  }
}
